import React, { useEffect, useState } from "react";
import { useMap } from "maplibre-react-components";
import { useUserDataContext } from "@/store/UserProvider";
import { useTariffLayer } from "@/store/mode";
import { useTranslation } from "react-i18next";
import { isKuperTariffMode, TariffMode } from "@/types/base";
import { getTariffModeConfig } from "@/utils/tariffModeConfig";
import cls from "./styles.module.scss";

interface TimeRange {
  min?: string;
  max?: string;
}

const getLastUpdateProperty = (tariffLayer: TariffMode) => {
  if (tariffLayer.startsWith("t_")) return "taxi_last_update";
  if (isKuperTariffMode(tariffLayer)) return "kuper_last_update";
  return "courier_last_update";
};

export default function LastUpdateTime() {
  const { t } = useTranslation();
  const { user } = useUserDataContext();
  const map = useMap();
  const [range, setRange] = useState<TimeRange>({});
  const { tariffLayer } = useTariffLayer();

  const isTaxi = tariffLayer.startsWith("t_");
  const { useSnapshots } = getTariffModeConfig(tariffLayer);
  const lastUpdateProperty = getLastUpdateProperty(tariffLayer);

  const blockUsers =
    user?.telegram_info.username !== "fionitkaW" &&
    user?.telegram_info.username !== "Devhoka" &&
    user?.telegram_info.username !== "Dyrhlahok" &&
    user?.telegram_info.username !== "evgeny_jke";

  useEffect(() => {
    if (!map) return;

    function update() {
      const features = map.queryRenderedFeatures(undefined, {
        layers: ["hex-fill"],
      });

      const { minIso, maxIso } = features.reduce(
        (acc, feat) => {
          const raw = feat.properties?.[lastUpdateProperty];
          if (typeof raw === "string") {
            const ts = Date.parse(raw);
            if (!Number.isNaN(ts)) {
              if (ts < acc.minTs) {
                acc.minTs = ts;
                acc.minIso = raw;
              }
              if (ts > acc.maxTs) {
                acc.maxTs = ts;
                acc.maxIso = raw;
              }
            }
          }
          return acc;
        },
        {
          minTs: Infinity,
          maxTs: -Infinity,
          minIso: undefined as string | undefined,
          maxIso: undefined as string | undefined,
        }
      );

      setRange({ min: minIso, max: maxIso });
    }

    map.on("load", update);
    map.on("moveend", update);
    update();

    return () => {
      map.off("load", update);
      map.off("moveend", update);
    };
  }, [map, lastUpdateProperty]);

  const format = (iso?: string): string =>
    iso
      ? (() => {
          const d = new Date(iso);
          const date = d.toLocaleDateString("ru-RU", {
            day: "2-digit",
            month: "2-digit",
          });
          const time = d.toLocaleTimeString("ru-RU", {
            hour: "2-digit",
            minute: "2-digit",
          });
          return `${date} в ${time}`;
        })()
      : "—";

  if (useSnapshots) return null;

  return (
    <div className={cls.lastUpdateBlock}>
      <div className={cls.lastUpdate}>
        {blockUsers && (
          <div className={cls.time}>
            {t("lastUpdate.updated")}: {format(range.min)}
          </div>
        )}
        {!isTaxi && (
          <div className={cls.kefInfo}>{t("lastUpdate.corporateOrders")}</div>
        )}
      </div>
    </div>
  );
}
